<!--
  ~ Copyright (c) 2023 NEXTWELL INTL SRL. All Rights Reserved.
  ~ This software is the proprietary information of NEXTWELL INTL SRL.
  ~ Use is subject to license and non-disclosure terms
  -->

<div class="card">
  <form (ngSubmit)="onSubmit()" [formGroup]="rolesForm">

    <p-table [value]="roleRightDefinitions"
             [lazy]="false"
             [scrollable]="true"
             [columns]="tableColumns"
             [scrollHeight]="'flex'" data-testid="table">
      <ng-template pTemplate="header" let-columns>

        <tr>
          <ng-container *ngFor="let col of columns; let i = index">

            <ng-container *ngIf="i === 0">
              <th pFrozenColumn>{{'auth.name' | translate}}</th>
            </ng-container>

            <ng-container *ngIf="i === 1">
              <th pFrozenColumn>{{'auth.description' | translate}}</th>
            </ng-container>

            <ng-container *ngIf="i === 2">
              <th pFrozenColumn class="in-progress-role-highlight">
                <div class="d-flex flex-column">
                  <input type="text" pInputText size="40"
                                                [placeholder]="'auth.role.newRole' | translate"
                         formControlName="name"
                         required
                         formInlineError="auth.role.configuration.errors"
                   data-testid="name-input">
                </div>
              </th>
            </ng-container>

            <ng-container *ngIf="i > 2" >

              <th [ngClass]="{'platform-role-highlight': col.platformRole}">
                <div class="d-flex">
                  <span class="center">{{col.name}}</span>

                    <button pButton
                            type="button"
                            [pTooltip]="'auth.role.cloneRole' | translate"
                            icon="ui-icon-content-copy"
                            class="p-button-text full-width"
                            (click)="onDuplicate(col.uuid)" data-testid="duplicate-btn">
                    </button>

                  <ng-container *ngIf="!col.platformRole || isPlatformAdmin ">
                    <button pButton
                            type="button"
                            [pTooltip]="'auth.role.editRole' | translate"
                            icon="ui-icon-edit"
                            class="p-button-text full-width"
                            (click)="onEdit(col.uuid)" data-testid="edit-btn">
                    </button>
                  </ng-container>
                </div>

              </th>

            </ng-container>

          </ng-container>

        </tr>

      </ng-template>

      <ng-template let-right pTemplate="body" let-columns="columns">

        <tr *ngIf="right.rightName !== 'ROLE_SELECT_ITEMS_ADMIN'">

          <ng-container *ngFor="let col of columns; let i = index">

            <ng-container *ngIf="i === 0">
              <td pFrozenColumn>{{right.rightLabel}}</td>
            </ng-container>

            <ng-container *ngIf="i === 1">
              <td pFrozenColumn>{{right.description}}</td>
            </ng-container>

            <ng-container *ngIf="i === 2">
              <td pFrozenColumn class="in-progress-role-highlight">
                <p-checkbox [binary]="true" [(ngModel)]="right.selected" [readonly]="isRightReadonly(right)" [ngModelOptions]="{standalone: true}" data-testid="selected-checkbox">
                </p-checkbox>
              </td>
            </ng-container>

            <ng-container *ngIf="i > 2">
              <td [ngClass]="{'platform-role-highlight': col.platformRole}">
                  <p-checkbox styleClass="readonly-checkbox" [binary]="true" [readonly]="true" [(ngModel)]="assignedRightsMap[right.rightName][col.uuid]"
                              [ngModelOptions]="{standalone: true}" data-testid="checkbox">
                  </p-checkbox>
              </td>

            </ng-container>

          </ng-container>

        </tr>
      </ng-template>

      <ng-template let-columns pTemplate="footer">
        <tr>

          <ng-container *ngFor="let col of columns; let i = index">

            <ng-container *ngIf="i === 0">
              <td pFrozenColumn>{{'auth.description' | translate}}</td>
            </ng-container>

            <ng-container *ngIf="i === 1">
              <td pFrozenColumn></td>
            </ng-container>

            <ng-container *ngIf="i === 2">
              <td pFrozenColumn>
                <textarea style="width: 100%"
                          pTextarea formControlName="description"
                          [rows]="3"
                          [placeholder]="'auth.role.descriptionPlaceholder' | translate" data-testid="description-textarea"></textarea>
              </td>
            </ng-container>

            <ng-container *ngIf="i > 2">
              <td>
                {{col.description}}
              </td>
            </ng-container>
          </ng-container>

        </tr>


        <tr *ngIf="getPlatformRoleSwitchValue() && showCompanyTypeDropdownForPlatform">
          <ng-container *ngFor="let col of columns; let i = index">

            <ng-container *ngIf="i === 0">
              <td pFrozenColumn>{{'auth.companyType' | translate}}</td>
            </ng-container>

            <ng-container *ngIf="i === 1">
              <td pFrozenColumn></td>
            </ng-container>

            <ng-container *ngIf="i === 2">
              <td pFrozenColumn *ngIf="!editMode">
                <p-select
                  [style]="{'width': '100%'}"
                  scrollHeight="300px" appendTo="body"
                  formControlName="companyType"
                  [options]="companyTypeOptions"
                  [autoDisplayFirst]="false"
                 data-testid="company-type-select">
                </p-select>
              </td>

              <td style="min-width: 15%" pFrozenColumn *ngIf="editMode">
                {{companyTypeTranslationMap[editedRole.companyType] ?? editedRole.companyType}}
              </td>
            </ng-container>

            <ng-container *ngIf="i > 2">
              <td>
                {{companyTypeTranslationMap[col.companyType] ?? col.companyType}}
              </td>
            </ng-container>
          </ng-container>
        </tr>
      </ng-template>


    </p-table>
    <div class="mt d-flex">
      <h5 class="mr">{{'auth.role.isPlatform' | translate}}</h5>
      <div>
        <p-toggleswitch
          formControlName="platformRole"
          [readonly]="true"
         data-testid="platform-role-toggle"></p-toggleswitch>
      </div>
    </div>
    <div class="d-flex justify-end">
      <ng-container *ngIf="editedRole && editedRole.uuid && !editedRole.initialRole ">
        <button [label]="'auth.delete' | translate" pButton class="mt mr p-button-outlined" type="button" (click)="onDelete(editedRole.uuid)" data-testid="delete-btn"></button>
      </ng-container>
      <button class="mt" pButton [label]="'auth.save' | translate" type="submit" data-testid="save-btn"></button>

    </div>
  </form>
</div>
<p-confirmDialog header="{{'auth.role.confirmDeletion' | translate}}"
                 acceptLabel="{{'auth.yes' | translate}}"
                 rejectLabel="{{'auth.no' | translate}}"
                 icon="fa fa-exclamation-triangle"
                 appendTo="body">
</p-confirmDialog>

